import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Calendar, Plus, Pencil, Trash2, Users, Clock, MapPin, Loader2, Lock, Globe, School, Building2, Video, Search, Check, ChevronsUpDown, Mail, Phone, Eye, AlertTriangle, Info, Link2 } from 'lucide-react';
import { formatTime } from '../../utils/dateFormat';

/** Count class meetings between startDate and endDate on the given weekdays. */
function calcMeetingCount(startDate, endDate, meetingDays) {
  if (!startDate || !endDate || !meetingDays?.length) return null;
  const DAY_MAP = { Monday: 1, Tuesday: 2, Wednesday: 3, Thursday: 4, Friday: 5, Saturday: 6, Sunday: 0 };
  const targets = new Set(meetingDays.map(d => DAY_MAP[d]).filter(n => n !== undefined));
  if (!targets.size) return null;
  let count = 0;
  const end = new Date(endDate + 'T00:00');
  for (let d = new Date(startDate + 'T00:00'); d <= end; d.setDate(d.getDate() + 1)) {
    if (targets.has(d.getDay())) count++;
  }
  return count;
}

export default function AdminSectionsPage({ embedded }) {
  const { api } = useAuth();
  const [classSearch, setClassSearch] = useState('');
  const [termFilter, setTermFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  const [instructorFilter, setInstructorFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [privateFilter, setPrivateFilter] = useState('all');
  const [courseSearchOpen, setCourseSearchOpen] = useState(false);
  const [courseSearchQuery, setCourseSearchQuery] = useState('');

  // Close course search dropdown on outside click
  useEffect(() => {
    if (!courseSearchOpen) return;
    const handleClick = (e) => {
      if (!e.target.closest('[data-course-search]')) setCourseSearchOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [courseSearchOpen]);
  const [sections, setSections] = useState([]);
  const [courses, setCourses] = useState([]);
  const [locations, setLocations] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [kits, setKits] = useState([]);
  const [availableTerms, setAvailableTerms] = useState([]);
  const [customTerm, setCustomTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [rosterDialog, setRosterDialog] = useState({ open: false, section: null, enrollments: [], loading: false });
  const [formData, setFormData] = useState({
    course: '',
    location: '',
    instructor: '',
    term: '',
    room: '',
    start_date: '',
    end_date: '',
    meeting_days: [],
    start_time: '',
    end_time: '',
    lesson_duration: '',
    capacity: 20,
    price: '',
    active: true,
    is_private: false,
    kit: '',
    kit_requirement: 'none',
    enrollment_restriction: 'open',
    delivery_type: 'in_person',
    meeting_link: '',
    meeting_password: '',
    grade_bands: [],
    label: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [sectionsRes, coursesRes, locationsRes, instructorsRes, kitsRes, termsRes] = await Promise.all([
        api.get('/sections/?active_only=false&include_private=true&section_type=class&page_size=5000'),
        api.get('/courses/?page_size=5000&active_only=false'),
        api.get('/locations/'),
        api.get('/users/?role=instructor'),
        api.get('/kits/'),
        api.get('/sections/terms/').catch(() => ({ data: [] })),
      ]);
      setSections(sectionsRes.data?.results || sectionsRes.data || []);
      setCourses(coursesRes.data?.results || coursesRes.data || []);
      setLocations(locationsRes.data?.results || locationsRes.data || []);
      setInstructors(instructorsRes.data?.results || instructorsRes.data || []);
      setKits(kitsRes.data?.results || kitsRes.data || []);
      setAvailableTerms(Array.isArray(termsRes.data) ? termsRes.data : []);
    } catch (error) {
      toast.error('Failed to load sections');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoster = async (section) => {
    setRosterDialog({ open: true, section, enrollments: [], loading: true });
    try {
      const res = await api.get(`/enrollments/?section_id=${section.id}&page_size=100`);
      const enrollments = res.data?.results || res.data || [];
      setRosterDialog(prev => ({ ...prev, enrollments, loading: false }));
    } catch {
      setRosterDialog(prev => ({ ...prev, loading: false }));
      toast.error('Failed to load roster');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!formData.start_time) { toast.error('Start time is required.'); return; }
      if (!formData.end_time) { toast.error('End time is required.'); return; }
      const mc = calcMeetingCount(formData.start_date, formData.end_date, formData.meeting_days);
      const sessionDuration = (() => {
        if (!formData.start_time || !formData.end_time) return null;
        const [sh, sm] = formData.start_time.split(':').map(Number);
        const [eh, em] = formData.end_time.split(':').map(Number);
        const diff = (eh * 60 + em) - (sh * 60 + sm);
        return diff > 0 ? diff : null;
      })();
      const ns = sessionDuration && formData.lesson_duration
        ? Math.floor(sessionDuration / formData.lesson_duration)
        : null;
      if (formData.lesson_duration && sessionDuration && sessionDuration % formData.lesson_duration !== 0) {
        toast.error(`Lesson duration (${formData.lesson_duration} min) does not evenly divide the session duration (${sessionDuration} min). Please adjust.`);
        return;
      }
      const payload = {
        ...formData,
        instructor: formData.instructor || null,
        location: formData.delivery_type === 'online' ? null : (formData.location || null),
        meeting_days: formData.meeting_days.length ? formData.meeting_days : ['Monday', 'Wednesday'],
        section_type: 'class',
        total_sessions: mc ?? 0,
        lesson_duration: formData.lesson_duration || null,
        num_lessons: ns,
        price: parseFloat(formData.price) || parseFloat(course?.base_price) || 0,
        kit_requirement_override: formData.kit_requirement || 'inherit',
      };
      delete payload.kit;
      delete payload.kit_requirement;
      
      if (editingSection) {
        await api.put(`/sections/${editingSection.id}/`, payload);
        toast.success('Session updated');
      } else {
        await api.post('/sections/', payload);
        toast.success('Session created');
      }
      
      // Update kit association on the course (if kit selected)
      if (formData.course && formData.kit) {
        try {
          await api.patch(`/courses/${formData.course}/`, {
            kit: formData.kit || null,
            kit_requirement: formData.kit_requirement || 'none',
          });
        } catch {}
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save session');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this class?')) return;
    try {
      await api.delete(`/sections/${id}/`);
      toast.success('Class deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete class');
    }
  };

  const resetForm = () => {
    setCustomTerm('');
    setFormData({
      course: '',
      location: '',
      instructor: '',
      term: '',
      room: '',
      start_date: '',
      end_date: '',
      meeting_days: [],
      start_time: '',
      end_time: '',
      lesson_duration: '',
      capacity: 20,
      price: '',
      active: true,
      is_private: false,
      kit: '',
      kit_requirement: 'none',
      enrollment_restriction: 'open',
      delivery_type: 'in_person',
      meeting_link: '',
      meeting_password: '',
      grade_bands: [],
      label: ''
    });
    setEditingSection(null);
  };

  const openEditDialog = (section) => {
    const termInList = availableTerms.includes(section.term);
    setCustomTerm(termInList ? '' : (section.term || ''));
    setEditingSection(section);
    setFormData({
      course: section.course,
      location: section.location || '',
      instructor: section.instructor || '',
      term: section.term,
      room: section.room || '',
      start_date: section.start_date,
      end_date: section.end_date,
      meeting_days: Array.isArray(section.meeting_days) ? section.meeting_days : (section.meeting_days ? [section.meeting_days] : []),
      start_time: section.start_time,
      end_time: section.end_time,
      lesson_duration: section.lesson_duration || '',
      capacity: section.capacity,
      price: section.price,
      active: section.active,
      is_private: section.is_private || false,
      kit: (() => { const c = courses.find(x => x.id === section.course); return c?.kit || ''; })(),
      kit_requirement: (() => { const c = courses.find(x => x.id === section.course); return c?.kit_requirement || 'none'; })(),
      enrollment_restriction: (() => {
        const loc = locations.find(l => l.id === (section.location || ''));
        if (loc?.partner_school) return 'school_only';
        if (loc?.park_district || loc?.library) return 'open';
        return section.enrollment_restriction || 'open';
      })(),
      delivery_type: section.delivery_type || 'in_person',
      meeting_link: section.meeting_link || '',
      meeting_password: section.meeting_password || '',
      grade_bands: Array.isArray(section.grade_bands) ? section.grade_bands : [],
      label: section.label || ''
    });
    setDialogOpen(true);
  };

  const toggleDay = (day) => {
    setFormData(prev => ({
      ...prev,
      meeting_days: prev.meeting_days.includes(day)
        ? prev.meeting_days.filter(d => d !== day)
        : [...prev.meeting_days, day]
    }));
  };

  const toggleGradeBand = (band) => {
    setFormData(prev => ({
      ...prev,
      grade_bands: prev.grade_bands.includes(band)
        ? prev.grade_bands.filter(b => b !== band)
        : [...prev.grade_bands, band]
    }));
  };

  const filteredSections = useMemo(() => sections.filter(s => {
    if (termFilter !== 'all' && s.term !== termFilter) return false;
    if (locationFilter !== 'all' && s.location_name !== locationFilter) return false;
    if (instructorFilter !== 'all' && s.instructor_name !== instructorFilter) return false;
    if (statusFilter === 'active' && !s.active) return false;
    if (statusFilter === 'inactive' && s.active) return false;
    if (privateFilter === 'public' && s.is_private) return false;
    if (privateFilter === 'private' && !s.is_private) return false;
    if (classSearch) {
      const q = classSearch.toLowerCase();
      return (s.course_title || '').toLowerCase().includes(q) ||
             (s.term || '').toLowerCase().includes(q) ||
             (s.location_name || '').toLowerCase().includes(q) ||
             (s.instructor_name || '').toLowerCase().includes(q) ||
             (s.meeting_days || '').toString().toLowerCase().includes(q);
    }
    return true;
  }), [sections, classSearch, termFilter, locationFilter, instructorFilter, statusFilter, privateFilter]);

  // ── Computed scheduling values (live as form fields change) ──────────
  const selectedCourse = useMemo(() => courses.find(c => c.id === formData.course), [courses, formData.course]);
  const meetingCount = useMemo(
    () => calcMeetingCount(formData.start_date, formData.end_date, formData.meeting_days),
    [formData.start_date, formData.end_date, formData.meeting_days],
  );
  const sessionDurationMinutes = useMemo(() => {
    if (!formData.start_time || !formData.end_time) return null;
    const [sh, sm] = formData.start_time.split(':').map(Number);
    const [eh, em] = formData.end_time.split(':').map(Number);
    const diff = (eh * 60 + em) - (sh * 60 + sm);
    return diff > 0 ? diff : null;
  }, [formData.start_time, formData.end_time]);
  const numSessions = useMemo(() => {
    if (!sessionDurationMinutes || !formData.lesson_duration) return null;
    return Math.floor(sessionDurationMinutes / formData.lesson_duration);
  }, [sessionDurationMinutes, formData.lesson_duration]);
  const isDivisible = useMemo(() => {
    if (!sessionDurationMinutes || !formData.lesson_duration) return true;
    return sessionDurationMinutes % formData.lesson_duration === 0;
  }, [sessionDurationMinutes, formData.lesson_duration]);
  // ─────────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!formData.start_time || !selectedCourse?.duration_minutes) return;
    const [h, m] = formData.start_time.split(':').map(Number);
    const total = h * 60 + m + selectedCourse.duration_minutes;
    const endH = Math.floor(total / 60) % 24;
    const endM = total % 60;
    setFormData(prev => ({ ...prev, end_time: `${String(endH).padStart(2, '0')}:${String(endM).padStart(2, '0')}` }));
  }, [formData.start_time, selectedCourse]);

  // Get location info to determine if restriction can be changed
  const getSelectedLocation = () => {
    return locations.find(l => l.id === formData.location);
  };

  const getRestrictionBadge = (section) => {
    if (section.enrollment_restriction === 'school_only') {
      return (
        <Badge variant="outline" className="bg-blue-50 text-blue-700">
          <School className="h-3 w-3 mr-1" />
          {section.restricted_to_school_name || 'School Only'}
        </Badge>
      );
    }
    if (section.enrollment_restriction === 'district_only') {
      return (
        <Badge variant="outline" className="bg-purple-50 text-purple-700">
          <Building2 className="h-3 w-3 mr-1" />
          {section.restricted_to_district_name || 'District Only'}
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="bg-green-50 text-green-700">
        <Globe className="h-3 w-3 mr-1" />
        Open
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const selectedLocation = getSelectedLocation();

  return (
    <div className="space-y-6" data-testid="admin-sections-page">
      <div className="flex justify-between items-center">
        {!embedded && (
        <div>
          <h1 className="font-heading text-2xl font-bold">Classes</h1>
          <p className="text-muted-foreground">Manage classes sessions</p>
        </div>
        )}
        <div className={embedded ? 'ml-auto' : ''}>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm} data-testid="add-section-btn">
              <Plus className="h-4 w-4 mr-2" /> Add Class
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingSection ? 'Edit Class' : 'Add New Class'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Course *</Label>
                  <div className="relative" data-course-search>
                    <button type="button" onClick={() => { setCourseSearchOpen(!courseSearchOpen); setCourseSearchQuery(''); }}
                      className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background hover:bg-accent"
                      data-testid="course-select">
                      <span className={`truncate ${formData.course ? '' : 'text-muted-foreground'}`}>
                        {formData.course ? (courses.find(c => c.id === formData.course)?.title || 'Select course') : 'Search and select a course...'}
                      </span>
                      <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
                    </button>
                    {courseSearchOpen && (
                      <div className="absolute z-50 mt-1 w-full min-w-[400px] rounded-md border bg-popover shadow-lg">
                        <div className="p-2 border-b">
                          <div className="relative">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                              placeholder="Type to search courses..."
                              value={courseSearchQuery}
                              onChange={e => setCourseSearchQuery(e.target.value)}
                              className="pl-9 h-9"
                              autoFocus
                            />
                          </div>
                        </div>
                        <div className="max-h-[250px] overflow-auto p-1">
                          {courses
                            .filter(c => c.active === true)
                            .filter(c => !courseSearchQuery || c.title.toLowerCase().includes(courseSearchQuery.toLowerCase()) || (c.category || '').toLowerCase().includes(courseSearchQuery.toLowerCase()) || (c.subcategory || '').toLowerCase().includes(courseSearchQuery.toLowerCase()))
                            .map(c => (
                              <button key={c.id} type="button"
                                className={`flex items-center gap-2 w-full px-3 py-2 text-sm rounded-md hover:bg-accent text-left transition-colors ${formData.course === c.id ? 'bg-primary/10 text-primary font-medium' : ''}`}
                                onClick={() => { setFormData({...formData, course: c.id, price: !editingSection ? (c.base_price ?? '') : formData.price, grade_bands: []}); setCourseSearchOpen(false); setCourseSearchQuery(''); }}>
                                {formData.course === c.id ? <Check className="h-4 w-4 text-primary shrink-0" /> : <div className="w-4" />}
                                <span className="truncate flex-1">{c.title}</span>
                                {c.category && <span className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded shrink-0">{c.category}</span>}
                              </button>
                            ))
                          }
                          {courses.filter(c => !courseSearchQuery || c.title.toLowerCase().includes(courseSearchQuery.toLowerCase()) || (c.category || '').toLowerCase().includes(courseSearchQuery.toLowerCase())).length === 0 && (
                            <p className="text-sm text-muted-foreground text-center py-4">No courses match "{courseSearchQuery}"</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <Label>Location *</Label>
                  <Select value={formData.location} onValueChange={(v) => {
                    const loc = locations.find(l => l.id === v);
                    const restriction = loc?.partner_school
                      ? 'school_only'
                      : (loc?.park_district || loc?.library)
                        ? 'open'
                        : formData.enrollment_restriction;
                    setFormData({...formData, location: v, enrollment_restriction: restriction});
                  }}>
                    <SelectTrigger data-testid="location-select"><SelectValue placeholder="Select location" /></SelectTrigger>
                    <SelectContent>
                      {locations.map(l => (
                        <SelectItem key={l.id} value={l.id}>
                          {l.name} {l.partner_school_name ? `(${l.partner_school_name})` : l.park_district_name ? `(${l.park_district_name})` : l.library_name ? `(${l.library_name})` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Instructor</Label>
                  <Select value={formData.instructor || 'unassigned'} onValueChange={(v) => setFormData({...formData, instructor: v === 'unassigned' ? '' : v})}>
                    <SelectTrigger><SelectValue placeholder="Select instructor" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">Unassigned</SelectItem>
                      {instructors.map(i => (
                        <SelectItem key={i.id} value={i.id}>{i.first_name} {i.last_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Term *</Label>
                  <Select
                    value={formData.term === customTerm && customTerm ? '__custom__' : formData.term}
                    onValueChange={(v) => {
                      if (v === '__custom__') {
                        setCustomTerm('');
                        setFormData(f => ({ ...f, term: '' }));
                      } else {
                        setCustomTerm('');
                        setFormData(f => ({ ...f, term: v }));
                      }
                    }}
                  >
                    <SelectTrigger><SelectValue placeholder="Select a term…" /></SelectTrigger>
                    <SelectContent>
                      {availableTerms.map(t => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                      {availableTerms.length > 0 && <div className="my-1 border-t" />}
                      <SelectItem value="__custom__">New term…</SelectItem>
                    </SelectContent>
                  </Select>
                  {(formData.term === '' || (customTerm && formData.term === customTerm)) && (
                    <Input
                      className="mt-2"
                      placeholder="e.g. Fall 2026"
                      value={customTerm}
                      onChange={e => {
                        setCustomTerm(e.target.value);
                        setFormData(f => ({ ...f, term: e.target.value }));
                      }}
                      autoFocus
                    />
                  )}
                </div>
                <div>
                  <Label>Delivery Type *</Label>
                  <Select 
                    value={formData.delivery_type} 
                    onValueChange={(v) => setFormData({...formData, delivery_type: v})}
                  >
                    <SelectTrigger data-testid="delivery-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="in_person">In-Person</SelectItem>
                      <SelectItem value="online">Online</SelectItem>
                      <SelectItem value="hybrid">Hybrid</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Start Date *</Label>
                  <Input type="date" value={formData.start_date} onChange={(e) => setFormData({...formData, start_date: e.target.value})} required />
                </div>
                <div>
                  <Label>End Date *</Label>
                  <Input type="date" value={formData.end_date} onChange={(e) => setFormData({...formData, end_date: e.target.value})} required />
                </div>
                <div>
                  <Label>Start Time *</Label>
                  <Input type="time" value={formData.start_time} onChange={(e) => setFormData({...formData, start_time: e.target.value})} required />
                </div>
                <div>
                  <Label>Lesson Duration (minutes)</Label>
                  <Input
                    type="number"
                    min="15"
                    step="5"
                    value={formData.lesson_duration || ''}
                    onChange={(e) => {
                      const dur = parseInt(e.target.value) || 0;
                      setFormData({ ...formData, lesson_duration: dur });
                    }}
                    placeholder="e.g. 60, 90, 120"
                  />
                </div>
                <div>
                  <Label>End Time *</Label>
                  <Input type="time" value={formData.end_time} onChange={(e) => setFormData({...formData, end_time: e.target.value})} required />
                </div>

                {/* ── Auto-calculated scheduling summary ── */}
                {(meetingCount !== null || numSessions !== null) && (
                  <div className="col-span-2 rounded-lg border p-3 space-y-2 bg-slate-50">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-1">
                      <Info className="w-3 h-3" /> Auto-calculated
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                      {meetingCount !== null && (
                        <div>
                          <p className="text-[11px] text-slate-500">Total Sessions</p>
                          <p className="text-xl font-bold text-slate-800">{meetingCount}</p>
                          <p className="text-[10px] text-slate-400">class sessions this term</p>
                        </div>
                      )}
                      {formData.lesson_duration > 0 && (
                        <div>
                          <p className="text-[11px] text-slate-500">Lessons</p>
                          {!sessionDurationMinutes ? (
                            <p className="text-[11px] text-amber-600 flex items-center gap-1 mt-1">
                              <AlertTriangle className="w-3 h-3" />
                              Set start and end time to calculate
                            </p>
                          ) : numSessions !== null && (
                            <>
                              <p className={`text-xl font-bold ${isDivisible ? 'text-slate-800' : 'text-orange-600'}`}>
                                {numSessions}
                              </p>
                              {isDivisible ? (
                                <p className="text-[10px] text-slate-400">
                                  {sessionDurationMinutes} min ÷ {formData.lesson_duration} min/lesson
                                </p>
                              ) : (
                                <p className="text-[10px] text-orange-500 flex items-center gap-1">
                                  <AlertTriangle className="w-3 h-3" />
                                  {sessionDurationMinutes} min doesn't divide evenly by {formData.lesson_duration}
                                </p>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                <div>
                  <Label>Capacity</Label>
                  <Input type="number" value={formData.capacity} onChange={(e) => setFormData({...formData, capacity: parseInt(e.target.value)})} />
                </div>

                <div>
                  <Label>Price ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.price}
                    onChange={(e) => setFormData({...formData, price: e.target.value})}
                    placeholder="Auto-filled from course"
                    data-testid="price-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    {formData.course && courses.find(c => c.id === formData.course)?.base_price > 0
                      ? `Auto-filled from course ($${courses.find(c => c.id === formData.course).base_price}) — override if needed.`
                      : 'This course has no base price set — go to Admin Dashboard → Content → Courses to set one first, or enter a custom price above.'}
                  </p>
                </div>

                <div>
                  <Label>Room</Label>
                  <Input 
                    value={formData.room} 
                    onChange={(e) => setFormData({...formData, room: e.target.value})} 
                    placeholder="Room 101"
                    disabled={formData.delivery_type === 'online'}
                  />
                </div>
              </div>
              
              {/* Online Course Settings */}
              {(formData.delivery_type === 'online' || formData.delivery_type === 'hybrid') && (
                <div className="p-4 bg-blue-50 dark:bg-blue-950/30 rounded-lg space-y-4 border border-blue-200 dark:border-blue-800">
                  <Label className="font-semibold flex items-center gap-2 text-blue-700 dark:text-blue-300">
                    <Video className="h-4 w-4" />
                    Online Class Settings
                  </Label>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <Label>Meeting Link (Zoom/Google Meet)</Label>
                      <Input 
                        type="url"
                        value={formData.meeting_link} 
                        onChange={(e) => setFormData({...formData, meeting_link: e.target.value})} 
                        placeholder="https://zoom.us/j/123456789 or https://meet.google.com/xxx-xxxx-xxx"
                        data-testid="meeting-link-input"
                      />
                      <p className="text-xs text-muted-foreground mt-1">Students will receive this link in their enrollment confirmation</p>
                    </div>
                    <div>
                      <Label>Meeting Password (Optional)</Label>
                      <Input 
                        value={formData.meeting_password} 
                        onChange={(e) => setFormData({...formData, meeting_password: e.target.value})} 
                        placeholder="Meeting password if required"
                        data-testid="meeting-password-input"
                      />
                    </div>
                  </div>
                  {formData.delivery_type === 'online' && (
                    <p className="text-sm text-blue-600 dark:text-blue-400">
                      💡 Online classes don't require a physical location. Students can join from anywhere!
                    </p>
                  )}
                  {formData.delivery_type === 'hybrid' && (
                    <p className="text-sm text-blue-600 dark:text-blue-400">
                      💡 Hybrid classes combine in-person and online sessions. Make sure to set both the location and meeting link.
                    </p>
                  )}
                </div>
              )}
              
              {/* Enrollment Restriction */}
              <div className="p-4 bg-muted/30 rounded-lg space-y-3">
                <Label className="font-semibold flex items-center gap-2">
                  <Lock className="h-4 w-4" />
                  Enrollment Restriction
                </Label>
                <Select 
                  value={formData.enrollment_restriction} 
                  onValueChange={(v) => setFormData({...formData, enrollment_restriction: v})}
                  disabled={selectedLocation?.partner_school || selectedLocation?.park_district || selectedLocation?.library}
                >
                  <SelectTrigger data-testid="restriction-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Open to All Students</SelectItem>
                    <SelectItem value="school_only">School Students Only</SelectItem>
                    <SelectItem value="district_only">District Students Only</SelectItem>
                  </SelectContent>
                </Select>
                {selectedLocation?.partner_school && (
                  <p className="text-sm text-blue-600 flex items-center gap-1">
                    <School className="h-4 w-4" />
                    Location is at a partner school - defaults to school-only enrollment
                  </p>
                )}
                {(selectedLocation?.park_district || selectedLocation?.library) && (
                  <p className="text-sm text-green-600 flex items-center gap-1">
                    <Globe className="h-4 w-4" />
                    Location is at a park district/library - open enrollment
                  </p>
                )}
                {!selectedLocation?.partner_school && !selectedLocation?.park_district && !selectedLocation?.library && formData.location && (
                  <p className="text-sm text-muted-foreground">
                    Independent location - you can set custom enrollment rules
                  </p>
                )}
              </div>
              
              <div>
                <Label>Meeting Days</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map(day => (
                    <Badge
                      key={day}
                      variant={formData.meeting_days.includes(day) ? 'default' : 'outline'}
                      className="cursor-pointer"
                      onClick={() => toggleDay(day)}
                    >
                      {day.slice(0, 3)}
                    </Badge>
                  ))}
                </div>
              </div>

              {editingSection && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Active</Label>
                      <p className="text-xs text-muted-foreground">Inactive sessions are hidden from the browse catalog</p>
                    </div>
                    <Switch
                      checked={formData.active}
                      onCheckedChange={(checked) => {
                        if (!checked) {
                          const today = new Date().toISOString().split('T')[0];
                          const hasEnrollments = (editingSection.enrolled_count || 0) > 0;
                          const notEnded = !editingSection.end_date || editingSection.end_date >= today;
                          if (hasEnrollments && notEnded) {
                            toast.warning(`This session has ${editingSection.enrolled_count} enrolled student(s) and hasn't ended yet.`);
                          }
                        }
                        setFormData({...formData, active: checked});
                      }}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Private</Label>
                      <p className="text-xs text-muted-foreground">Hidden from catalog — share the direct link to enroll</p>
                    </div>
                    <Switch
                      checked={formData.is_private}
                      onCheckedChange={(checked) => setFormData({...formData, is_private: checked})}
                    />
                  </div>
                </div>
              )}

              {selectedCourse?.grade_bands?.length > 0 && (
                <div>
                  <Label>Grade Bands</Label>
                  <p className="text-xs text-muted-foreground mb-2">Select which grade bands this session targets (from the course's available bands)</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedCourse.grade_bands.map(band => (
                      <Badge
                        key={band}
                        variant={formData.grade_bands.includes(band) ? 'default' : 'outline'}
                        className="cursor-pointer"
                        onClick={() => toggleGradeBand(band)}
                      >
                        {band}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <Label>Session Label (internal)</Label>
                <Input
                  value={formData.label}
                  onChange={(e) => setFormData({...formData, label: e.target.value})}
                  placeholder="e.g. Morning slot, Intensive, After-school"
                  maxLength={100}
                />
                <p className="text-xs text-muted-foreground mt-1">Optional — for admin reference only, not shown to parents</p>
              </div>

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit" data-testid="save-section-btn">{editingSection ? 'Update' : 'Create'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-5 gap-4">
        <Card className="card-dashboard">
          <CardContent className="p-4 text-center">
            <Calendar className="h-8 w-8 mx-auto mb-2 text-blue-600" />
            <p className="text-2xl font-bold">{sections.length}</p>
            <p className="text-sm text-muted-foreground">Total Classes</p>
          </CardContent>
        </Card>
        <Card className="card-dashboard">
          <CardContent className="p-4 text-center">
            <Video className="h-8 w-8 mx-auto mb-2 text-cyan-600" />
            <p className="text-2xl font-bold">{sections.filter(s => s.delivery_type === 'online' || s.delivery_type === 'hybrid').length}</p>
            <p className="text-sm text-muted-foreground">Online/Hybrid</p>
          </CardContent>
        </Card>
        <Card className="card-dashboard">
          <CardContent className="p-4 text-center">
            <Globe className="h-8 w-8 mx-auto mb-2 text-green-600" />
            <p className="text-2xl font-bold">{sections.filter(s => s.enrollment_restriction === 'open').length}</p>
            <p className="text-sm text-muted-foreground">Open Enrollment</p>
          </CardContent>
        </Card>
        <Card className="card-dashboard">
          <CardContent className="p-4 text-center">
            <School className="h-8 w-8 mx-auto mb-2 text-blue-600" />
            <p className="text-2xl font-bold">{sections.filter(s => s.enrollment_restriction === 'school_only').length}</p>
            <p className="text-sm text-muted-foreground">School Only</p>
          </CardContent>
        </Card>
        <Card className="card-dashboard">
          <CardContent className="p-4 text-center">
            <Users className="h-8 w-8 mx-auto mb-2 text-purple-600" />
            <p className="text-2xl font-bold">{sections.reduce((sum, s) => sum + s.enrolled_count, 0)}</p>
            <p className="text-sm text-muted-foreground">Total Enrolled</p>
          </CardContent>
        </Card>
      </div>

      {/* Search Bar */}
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by course, term, location, instructor..."
              value={classSearch}
              onChange={e => setClassSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          <span className="text-sm text-muted-foreground whitespace-nowrap">
            {filteredSections.length} of {sections.length} classes
          </span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={termFilter} onValueChange={setTermFilter}>
            <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Terms</SelectItem>
              {[...new Set(sections.map(s => s.term).filter(Boolean))].sort().map(t => (
                <SelectItem key={t} value={t}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={locationFilter} onValueChange={setLocationFilter}>
            <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Locations</SelectItem>
              {[...new Set(sections.map(s => s.location_name).filter(Boolean))].sort().map(l => (
                <SelectItem key={l} value={l}>{l}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={instructorFilter} onValueChange={setInstructorFilter}>
            <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Instructors</SelectItem>
              {[...new Set(sections.map(s => s.instructor_name).filter(Boolean))].sort().map(i => (
                <SelectItem key={i} value={i}>{i}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[130px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
          <Select value={privateFilter} onValueChange={setPrivateFilter}>
            <SelectTrigger className="w-[130px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Visibility</SelectItem>
              <SelectItem value="public">Public</SelectItem>
              <SelectItem value="private">Private</SelectItem>
            </SelectContent>
          </Select>
          {(classSearch || termFilter !== 'all' || locationFilter !== 'all' || instructorFilter !== 'all' || statusFilter !== 'all' || privateFilter !== 'all') && (
            <Button variant="ghost" size="sm" onClick={() => { setClassSearch(''); setTermFilter('all'); setLocationFilter('all'); setInstructorFilter('all'); setStatusFilter('all'); setPrivateFilter('all'); }}>
              Clear filters
            </Button>
          )}
        </div>
      </div>

      <Card className="card-dashboard">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Course</TableHead>
                <TableHead>Term</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Schedule</TableHead>
                <TableHead>Restriction</TableHead>
                <TableHead>Enrollment</TableHead>
                <TableHead>Price</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredSections.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                    {sections.length === 0 ? 'No sections found. Add your first section above.' : 'No sessions match the current filters.'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredSections.map((section) => (
                  <TableRow key={section.id} data-testid={`section-row-${section.id}`}>
                    <TableCell className="font-medium">{section.course_title}</TableCell>
                    <TableCell>{section.term}</TableCell>
                    <TableCell>
                      {section.delivery_type === 'online' ? (
                        <div className="flex items-center gap-1 text-blue-600">
                          <Video className="h-4 w-4" />
                          <span>Online</span>
                        </div>
                      ) : section.delivery_type === 'hybrid' ? (
                        <div>
                          <div className="flex items-center gap-1 text-purple-600">
                            <Video className="h-4 w-4" />
                            <span>Hybrid</span>
                          </div>
                          <div className="text-sm text-muted-foreground">{section.location_name}</div>
                        </div>
                      ) : (
                        <div>
                          <div>{section.location_name}</div>
                          {section.room && <div className="text-sm text-muted-foreground">Room: {section.room}</div>}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      <div>{Array.isArray(section.meeting_days) ? section.meeting_days.join(', ') : (section.meeting_days || 'TBD')}</div>
                      <div className="text-muted-foreground">{formatTime(section.start_time)} - {formatTime(section.end_time)}</div>
                    </TableCell>
                    <TableCell>{getRestrictionBadge(section)}</TableCell>
                    <TableCell>
                      <button
                        onClick={() => fetchRoster(section)}
                        className="flex items-center gap-1.5 hover:text-rsc-navy transition-colors cursor-pointer"
                        data-testid={`view-roster-${section.id}`}
                      >
                        <span className={`font-bold ${section.enrolled_count >= section.capacity ? 'text-red-600' : section.enrolled_count > 0 ? 'text-emerald-600' : ''}`}>
                          {section.enrolled_count}/{section.capacity}
                        </span>
                        {section.enrolled_count > 0 && <Eye className="h-3.5 w-3.5 text-muted-foreground" />}
                      </button>
                    </TableCell>
                    <TableCell>${(Number(section.price) || 0).toFixed(2)}</TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <Badge variant={section.active ? 'default' : 'secondary'}>
                          {section.active ? 'Active' : 'Inactive'}
                        </Badge>
                        {section.is_private && (
                          <Badge variant="outline" className="text-xs border-purple-400 text-purple-600 bg-purple-50">Private</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        {section.is_private && (
                          <Button
                            size="sm"
                            variant="ghost"
                            title="Copy direct link"
                            onClick={() => {
                              const url = `${window.location.origin}/courses/${section.course}?section=${section.id}`;
                              navigator.clipboard.writeText(url);
                              toast.success('Link copied to clipboard');
                            }}
                          >
                            <Link2 className="h-4 w-4 text-purple-500" />
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => openEditDialog(section)} data-testid={`edit-section-${section.id}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(section.id)} data-testid={`delete-section-${section.id}`}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Roster Dialog */}
      <Dialog open={rosterDialog.open} onOpenChange={(open) => !open && setRosterDialog(prev => ({ ...prev, open: false }))}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Enrolled Students — {rosterDialog.section?.course_title}
            </DialogTitle>
            <p className="text-sm text-muted-foreground">
              {rosterDialog.section?.location_name} · {rosterDialog.section?.term} · {rosterDialog.section?.enrolled_count}/{rosterDialog.section?.capacity} enrolled
            </p>
          </DialogHeader>
          {rosterDialog.loading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : rosterDialog.enrollments.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Users className="h-10 w-10 mx-auto mb-2 opacity-30" />
              <p className="font-medium">No students enrolled yet</p>
              <p className="text-sm">Enrollments will appear here as parents register.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Student</TableHead>
                  <TableHead>Grade</TableHead>
                  <TableHead>Parent</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Registered</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rosterDialog.enrollments.map(e => (
                  <TableRow key={e.id} data-testid={`roster-row-${e.id}`}>
                    <TableCell className="font-medium">{e.learner_name}</TableCell>
                    <TableCell>{e.learner_grade || '—'}</TableCell>
                    <TableCell>{e.parent_name || '—'}</TableCell>
                    <TableCell>
                      <div className="space-y-0.5">
                        {e.parent_email && (
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Mail className="h-3 w-3" /> {e.parent_email}
                          </div>
                        )}
                        {e.parent_phone && (
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Phone className="h-3 w-3" /> {e.parent_phone}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={e.status === 'confirmed' ? 'default' : e.status === 'waitlisted' ? 'outline' : 'secondary'}>
                        {e.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {e.created_at ? new Date(e.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
